import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getTruckById, updateTruck } from '../../store/trucks';
import { fetchBuildingsUser } from '../../store/buildings';
import { fetchUsersUser } from '../../store/users';
import LocationSelector from '../../components/reusable/LocationSelector';

const TRUCK_TYPES = {
  'daycab_1_axle': 'Daycab 1 Axle',
  'daycab_2_axle': 'Daycab 2 Axle',
  'sleeper_1_axle': 'Sleeper 1 Axle',
  'sleeper_2_axle': 'Sleeper 2 Axle',
  'boxtruck': 'Boxtruck'
};

// List of US states for the license state dropdown
const statesList = [
  { name: "Alabama", abbreviation: "AL" },
  { name: "Alaska", abbreviation: "AK" },
  { name: "Arizona", abbreviation: "AZ" },
  { name: "Arkansas", abbreviation: "AR" },
  { name: "California", abbreviation: "CA" },
  { name: "Colorado", abbreviation: "CO" },
  { name: "Connecticut", abbreviation: "CT" },
  { name: "Delaware", abbreviation: "DE" },
  { name: "Florida", abbreviation: "FL" },
  { name: "Georgia", abbreviation: "GA" },
  { name: "Hawaii", abbreviation: "HI" },
  { name: "Idaho", abbreviation: "ID" },
  { name: "Illinois", abbreviation: "IL" },
  { name: "Indiana", abbreviation: "IN" },
  { name: "Iowa", abbreviation: "IA" },
  { name: "Kansas", abbreviation: "KS" },
  { name: "Kentucky", abbreviation: "KY" },
  { name: "Louisiana", abbreviation: "LA" },
  { name: "Maine", abbreviation: "ME" },
  { name: "Maryland", abbreviation: "MD" },
  { name: "Massachusetts", abbreviation: "MA" },
  { name: "Michigan", abbreviation: "MI" },
  { name: "Minnesota", abbreviation: "MN" },
  { name: "Mississippi", abbreviation: "MS" },
  { name: "Missouri", abbreviation: "MO" },
  { name: "Montana", abbreviation: "MT" },
  { name: "Nebraska", abbreviation: "NE" },
  { name: "Nevada", abbreviation: "NV" },
  { name: "New Hampshire", abbreviation: "NH" },
  { name: "New Jersey", abbreviation: "NJ" },
  { name: "New Mexico", abbreviation: "NM" },
  { name: "New York", abbreviation: "NY" },
  { name: "North Carolina", abbreviation: "NC" },
  { name: "North Dakota", abbreviation: "ND" },
  { name: "Ohio", abbreviation: "OH" },
  { name: "Oklahoma", abbreviation: "OK" },
  { name: "Oregon", abbreviation: "OR" },
  { name: "Pennsylvania", abbreviation: "PA" },
  { name: "Rhode Island", abbreviation: "RI" },
  { name: "South Carolina", abbreviation: "SC" },
  { name: "South Dakota", abbreviation: "SD" },
  { name: "Tennessee", abbreviation: "TN" },
  { name: "Texas", abbreviation: "TX" },
  { name: "Utah", abbreviation: "UT" },
  { name: "Vermont", abbreviation: "VT" },
  { name: "Virginia", abbreviation: "VA" },
  { name: "Washington", abbreviation: "WA" },
  { name: "West Virginia", abbreviation: "WV" },
  { name: "Wisconsin", abbreviation: "WI" },
  { name: "Wyoming", abbreviation: "WY" },
  { name: "District of Columbia", abbreviation: "DC" },
];

function UpdateTruck() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const sessionUser = useSelector(state => state.session.user);
  const allUsers = useSelector(state => state.users);
  const buildings = useSelector((state) => state.buildings || []);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  // User dropdown state
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  // License state dropdown state
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState(statesList);
  const stateDropdownRef = useRef(null);

  // Type dropdown state
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const typeDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    assigned_user_id: '',
    truck_number: '',
    truck_location: '',
    type: '',
    license_plate: '',
    license_state: '',
    year: '',
    make: '',
    model: '',
    red_tagged: false,
    yellow_tagged: false,
    in_shop: false,
    tagged_description: '',
    last_inspection: '',
    next_inspection: '',
    last_maintenance: '',
    next_maintenance: ''
  });

  // Define available locations
  const locations = ['Yard', 'Road', 'Shop'];

  // Add useEffect to handle clicks outside the dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownRef, stateDropdownRef, typeDropdownRef]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const truckData = await dispatch(getTruckById(id));
        if (!truckData) {
          throw new Error("Failed to fetch truck data");
        }

        // Fetch buildings for the account
        if (truckData.account_id) {
          await dispatch(fetchBuildingsUser(truckData.account_id));
          // Also fetch all users for the account
          await dispatch(fetchUsersUser(truckData.account_id));
        }

        // Set the form data
        setFormData({
          ...truckData,
          tagged_description: truckData.tagged_description || '',
          last_inspection: truckData.last_inspection ? new Date(truckData.last_inspection).toISOString().split('T')[0] : '',
          next_inspection: truckData.next_inspection ? new Date(truckData.next_inspection).toISOString().split('T')[0] : '',
          last_maintenance: truckData.last_maintenance ? new Date(truckData.last_maintenance).toISOString().split('T')[0] : '',
          next_maintenance: truckData.next_maintenance ? new Date(truckData.next_maintenance).toISOString().split('T')[0] : ''
        });

        // Set selected building if truck location includes a building
        if (truckData.truck_location && !truckData.in_shop) {
          const [buildingName] = truckData.truck_location.split('-');
          const matchingBuilding = buildings.find(b => b.building_name === buildingName);
          if (matchingBuilding) {
            setSelectedBuilding(matchingBuilding);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, dispatch, sessionUser?.id]);

  // Filter users based on search term
  const filteredUsers = searchTerm
    ? allUsers.filter(user =>
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.alpha_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.driver_number && user.driver_number.toString().includes(searchTerm))
      )
    : allUsers;

  // Filter truck types based on search term
  const filteredTypes = typeSearchTerm
    ? Object.entries(TRUCK_TYPES).filter(([key, value]) =>
        value.toLowerCase().includes(typeSearchTerm.toLowerCase()) ||
        key.toLowerCase().includes(typeSearchTerm.toLowerCase())
      )
    : Object.entries(TRUCK_TYPES);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formattedData = {
        ...formData,
        year: parseInt(formData.year),
        assigned_user_id: formData.assigned_user_id ? parseInt(formData.assigned_user_id) : null,
        id: parseInt(id)
      };

      // Format dates if they exist
      ['last_inspection', 'next_inspection', 'last_maintenance', 'next_maintenance'].forEach(field => {
        if (formattedData[field]) {
          formattedData[field] = new Date(formattedData[field]).toISOString().split('T')[0];
        }
      });

      // Basic validation
      if (!formattedData.truck_number || !formattedData.truck_location ||
          !formattedData.type || !formattedData.license_plate ||
          !formattedData.license_state || !formattedData.year ||
          !formattedData.make || !formattedData.model) {
        window.alert('Please fill out all required fields');
        return;
      }

      // Validate tagged description if truck is tagged
      if ((formattedData.red_tagged || formattedData.yellow_tagged) && !formattedData.tagged_description) {
        window.alert('Tagged description is required when a truck is tagged');
        return;
      }

      const result = await dispatch(updateTruck(formattedData));
      if (result) {
        history.push(`/trucks/${id}`);
      }
    } catch (error) {
      console.error('Error updating truck:', error);
      window.alert('Failed to update truck. Please try again.');
    }
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({
      ...prev,
      red_tagged: status === 'red',
      yellow_tagged: status === 'yellow',
      in_shop: status === 'shop',
      // If in_shop is selected, automatically set location to "Shop"
      truck_location: status === 'shop' ? 'Shop' : prev.truck_location
    }));
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const name = e.target.name;

    if (name === 'red_tagged' && value === true) {
      // If red_tagged is checked, uncheck yellow_tagged
      setFormData(prev => ({
        ...prev,
        [name]: value,
        yellow_tagged: false
      }));
    } else if (name === 'yellow_tagged' && value === true) {
      // If yellow_tagged is checked, uncheck red_tagged
      setFormData(prev => ({
        ...prev,
        [name]: value,
        red_tagged: false
      }));
    } else if (name === 'in_shop' && value === true) {
      // If in_shop is checked, set location to "Shop"
      setFormData(prev => ({
        ...prev,
        [name]: value,
        truck_location: 'Shop'
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLocationChange = (locationType, value) => {
    if (locationType === 'building') {
      const building = buildings.find(b => b.building_name === value);
      setSelectedBuilding(building);
      // Clear the truck location until a door is selected
      setFormData(prev => ({
        ...prev,
        truck_location: '',
        in_shop: false
      }));
    } else {
      setSelectedBuilding(null);
      setFormData(prev => ({
        ...prev,
        truck_location: value,
        in_shop: value === 'Shop'
      }));
    }
  };

  const handleDoorSelection = (doorNumber) => {
    if (selectedBuilding) {
      setFormData(prev => ({
        ...prev,
        truck_location: `${selectedBuilding.building_name}-${doorNumber}`
      }));
    }
  };

  // Function to handle user selection
  const handleUserSelect = (userId) => {
    setFormData(prev => ({ ...prev, assigned_user_id: userId }));
    setShowUserDropdown(false);
    setSearchTerm('');
  };

  // Function to handle state selection
  const handleStateSelect = (abbreviation) => {
    setFormData(prev => ({ ...prev, license_state: abbreviation }));
    setShowStateDropdown(false);
    setStateSearchTerm('');
  };

  // Function to handle type selection
  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, type }));
    setShowTypeDropdown(false);
    setTypeSearchTerm('');
  };

  // Handle state search input change
  const handleStateInputChange = (e) => {
    const inputValue = e.target.value.toUpperCase();
    setStateSearchTerm(inputValue);
    setFormData(prev => ({ ...prev, license_state: inputValue }));

    if (inputValue) {
      setFilteredStates(
        statesList.filter(
          (state) =>
            state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            state.abbreviation.includes(inputValue)
        )
      );
      setShowStateDropdown(true);
    } else {
      setFilteredStates(statesList);
      setShowStateDropdown(inputValue === '');
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        <div className="ml-4 text-2xl text-blue-600">Loading...</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-8xl font-bold mb-10 text-center text-blue-600">Update Truck</h1>

        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Basic Information</h2>

              <div className="relative" ref={userDropdownRef}>
                <label htmlFor="assigned-driver" className="block text-2xl font-medium text-gray-700 mb-2">
                  Assigned Driver
                </label>
                <div className="relative">
                  {/* Search input */}
                  <input
                    id="assigned-driver-search"
                    type="text"
                    placeholder={formData.assigned_user_id ? allUsers.find(user => user.id === parseInt(formData.assigned_user_id))?.alpha_code || '' : "Search for a user..."}
                    value={showUserDropdown ? searchTerm : (formData.assigned_user_id ? `${allUsers.find(user => user.id === parseInt(formData.assigned_user_id))?.first_name || ''} ${allUsers.find(user => user.id === parseInt(formData.assigned_user_id))?.last_name || ''} (${allUsers.find(user => user.id === parseInt(formData.assigned_user_id))?.alpha_code || ''})` : '')}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={() => setShowUserDropdown(true)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                  {/* Dropdown arrow indicator */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>

                {/* Dropdown */}
                {showUserDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <div className="p-3 text-center text-gray-500">No users found</div>
                    ) : (
                      filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className={`p-3 cursor-pointer hover:bg-blue-100 focus:bg-blue-100 ${
                            parseInt(formData.assigned_user_id) === user.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                          }`}
                          onClick={() => handleUserSelect(user.id)}
                          tabIndex="0"
                          role="option"
                          aria-selected={parseInt(formData.assigned_user_id) === user.id}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium">{user.first_name} {user.last_name}</span>
                              <span className="ml-2 text-gray-600">({user.alpha_code})</span>
                            </div>
                            {user.driver_number && (
                              <span className="text-sm text-gray-500">Driver #{user.driver_number}</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="truck-number" className="block text-2xl font-medium text-gray-700 mb-2">
                  Truck Number
                </label>
                <input
                  id="truck-number"
                  type="text"
                  name="truck_number"
                  value={formData.truck_number}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <fieldset>
                  <legend className="block text-2xl font-medium text-gray-700 mb-2">
                    Location
                  </legend>
                  <LocationSelector
                    value={formData.truck_location}
                    onChange={(location) => setFormData({ ...formData, truck_location: location })}
                    className="text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </fieldset>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Status</h2>

              <div className="space-y-4">
                <div className="flex flex-col gap-4">
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      id="status-available"
                      name="status"
                      checked={!formData.red_tagged && !formData.yellow_tagged && !formData.in_shop}
                      onChange={() => handleStatusChange("")}
                      className="w-5 h-5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    Available
                  </label>
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      id="status-red"
                      name="status"
                      checked={formData.red_tagged}
                      onChange={() => handleStatusChange("red")}
                      className="w-5 h-5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    Red Tagged
                  </label>
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      id="status-yellow"
                      name="status"
                      checked={formData.yellow_tagged}
                      onChange={() => handleStatusChange("yellow")}
                      className="w-5 h-5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    Yellow Tagged
                  </label>
                  <label className="flex items-center gap-2 text-xl">
                    <input
                      type="radio"
                      id="status-shop"
                      name="status"
                      checked={formData.in_shop}
                      onChange={() => handleStatusChange("shop")}
                      className="w-5 h-5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    Shop
                  </label>
                </div>

                <div>
                  <label htmlFor="tagged-description" className="block text-2xl font-medium text-gray-700 mb-2">
                    Tagged Description
                  </label>
                  <textarea
                    id="tagged-description"
                    name="tagged_description"
                    value={formData.tagged_description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Vehicle Details</h2>
              <div>
                <label htmlFor="license-plate" className="block text-2xl font-medium text-gray-700 mb-2">
                  License Plate
                </label>
                <input
                  id="license-plate"
                  type="text"
                  name="license_plate"
                  value={formData.license_plate}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="relative" ref={stateDropdownRef}>
                <label htmlFor="license-state" className="block text-2xl font-medium text-gray-700 mb-2">
                  License State
                </label>
                <div className="relative">
                  <input
                    id="license-state"
                    type="text"
                    placeholder="Search for a state..."
                    value={showStateDropdown ? stateSearchTerm : formData.license_state}
                    onChange={handleStateInputChange}
                    onClick={() => setShowStateDropdown(true)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                  {/* Dropdown arrow indicator */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>

                {/* Dropdown */}
                {showStateDropdown && (
                  <div className="absolute z-10 w-full mt-0.5 top-full left-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredStates.length === 0 ? (
                      <div className="p-3 text-center text-gray-500">No states found</div>
                    ) : (
                      filteredStates.map(state => (
                        <div
                          key={state.abbreviation}
                          className={`p-3 cursor-pointer hover:bg-blue-100 focus:bg-blue-100 ${
                            formData.license_state === state.abbreviation ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                          }`}
                          onClick={() => handleStateSelect(state.abbreviation)}
                          tabIndex="0"
                          role="option"
                          aria-selected={formData.license_state === state.abbreviation}
                        >
                          <div className="flex justify-between">
                            <span>{state.name}</span>
                            <span className="font-bold">{state.abbreviation}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="vehicle-year" className="block text-2xl font-medium text-gray-700 mb-2">
                  Year
                </label>
                <input
                  id="vehicle-year"
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="vehicle-make" className="block text-2xl font-medium text-gray-700 mb-2">
                  Make
                </label>
                <input
                  id="vehicle-make"
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="vehicle-model" className="block text-2xl font-medium text-gray-700 mb-2">
                  Model
                </label>
                <input
                  id="vehicle-model"
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="relative" ref={typeDropdownRef}>
                <label htmlFor="vehicle-type" className="block text-2xl font-medium text-gray-700 mb-2">
                  Type
                </label>
                <div className="relative">
                  <input
                    id="vehicle-type-search"
                    type="text"
                    placeholder="Search for a truck type..."
                    value={showTypeDropdown ? typeSearchTerm : (formData.type ? TRUCK_TYPES[formData.type] || formData.type : '')}
                    onChange={(e) => setTypeSearchTerm(e.target.value)}
                    onClick={() => setShowTypeDropdown(true)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                  {/* Dropdown arrow indicator */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>

                {/* Dropdown */}
                {showTypeDropdown && (
                  <div className="absolute z-10 w-full mt-0.5 top-full left-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredTypes.length === 0 ? (
                      <div className="p-3 text-center text-gray-500">No truck types found</div>
                    ) : (
                      filteredTypes.map(([key, value]) => (
                        <div
                          key={key}
                          className={`p-3 cursor-pointer hover:bg-blue-100 focus:bg-blue-100 ${
                            formData.type === key ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                          }`}
                          onClick={() => handleTypeSelect(key)}
                          tabIndex="0"
                          role="option"
                          aria-selected={formData.type === key}
                        >
                          {value}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Maintenance Dates</h2>
              <div>
                <label htmlFor="last-inspection" className="block text-2xl font-medium text-gray-700 mb-2">
                  Last Inspection
                </label>
                <input
                  id="last-inspection"
                  type="date"
                  name="last_inspection"
                  value={formData.last_inspection}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="next-inspection" className="block text-2xl font-medium text-gray-700 mb-2">
                  Next Inspection
                </label>
                <input
                  id="next-inspection"
                  type="date"
                  name="next_inspection"
                  value={formData.next_inspection}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="last-maintenance" className="block text-2xl font-medium text-gray-700 mb-2">
                  Last Maintenance
                </label>
                <input
                  id="last-maintenance"
                  type="date"
                  name="last_maintenance"
                  value={formData.last_maintenance}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="next-maintenance" className="block text-2xl font-medium text-gray-700 mb-2">
                  Next Maintenance
                </label>
                <input
                  id="next-maintenance"
                  type="date"
                  name="next_maintenance"
                  value={formData.next_maintenance}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={() => history.push(`/trucks/${id}`)}
              className="px-6 py-3 text-xl text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              Cancel
            </button>
            <button id = 'update-truck-button'
              type="submit"
              className="px-6 py-3 text-xl bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              Update Truck
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateTruck;
